import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type JSX, useState } from 'react';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { Button } from 'Components/Buttons/Buttons';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_MOVE_OR_SCROLL_DOWN, ICON_MOVE_OR_SCROLL_UP } from 'Config/Icons';
import { UploadedFile } from 'Models/Files';

import styles from './AssociatedDocumentsModal.module.css';
import { Modal } from '../Modal';

const DEFAULT_NUMBER_OF_DOCUMENTS_SHOWN = 5;

export interface AssociatedDocumentsModalProps {
    associatedDocuments: UploadedFile[];
    documentApi: DocumentApi;
    hideModal: () => void;

    /**
     * If undefined, "Documents" will be the header text.
     */
    headerText?: string;
}

/**
 * This is a modal used to show the user the documents that are associated with an arbitrary entity. It is intended to be used in "listing" components, where one of the columns shows an "X Documents" button that opens this modal.
 * Renders a list of documents, if a non-zero number of documents are provided; otherwise, renders text informing the user that no documents are associated with the entity.
 *
 * IMPORTANT: This follows the same patterns as `CollapsibleModal`. If a change is made here, consider whether the same change needs to be made there, and vice-versa.
 */
export const AssociatedDocumentsModal = (props: AssociatedDocumentsModalProps): JSX.Element => {
    const [showAll, setShowAll] = useState<boolean>(false);

    const documentsToDisplay = showAll ? props.associatedDocuments : props.associatedDocuments.slice(0, DEFAULT_NUMBER_OF_DOCUMENTS_SHOWN);

    return (
        <Modal open header={props.headerText ?? 'Documents'}>
            {props.associatedDocuments.length > 0 ? (
                <div className={styles.documentList}>
                    {documentsToDisplay.map((file) => (
                        <div key={file.file_id}>
                            <UploadedFileAndState documentApi={props.documentApi} file={file} onDownloadError={console.log} />
                        </div>
                    ))}
                </div>
            ) : (
                <div>
                    <Text>No documents.</Text>
                </div>
            )}
            <div className={styles.showMoreButton}>
                {props.associatedDocuments.length > DEFAULT_NUMBER_OF_DOCUMENTS_SHOWN && (
                    <Button variant="linkText" size="lg" onClick={() => setShowAll(!showAll)}>
                        {showAll ? (
                            <>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_UP} />
                                {` Show Less`}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_DOWN} />
                                {` Show ${props.associatedDocuments.length - DEFAULT_NUMBER_OF_DOCUMENTS_SHOWN} More`}
                            </>
                        )}
                    </Button>
                )}
            </div>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
            </div>
        </Modal>
    );
};
